import React, { useEffect, useMemo, useState } from 'react'
import { getHeatMap } from 'client/utils/api-routes-analytics'
import moment from 'moment'
import { DcChart, DcDiv, colors } from 'pixel-base'
import type { AjaxResponse } from 'rxjs/ajax'

interface HeatingHourData {
  date: string
  count: number
  total: number
}

interface HeatingHoursProps {
  data: HeatingHourData[]
  loading: boolean
}

const HeatingHours: React.FC<HeatingHoursProps> = ({
  _data,
  _loading,
}: HeatingHoursProps) => {
  const [heatmapData, setHeatmapData] = useState<any[]>([])

  const getAllDashboardSummary = () => {
    getHeatMap({
      start_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
    }).subscribe((res: AjaxResponse<any>) => {
      const data = res.response?.data || []
      setHeatmapData(data)
    })
  }

  useEffect(() => {
    getAllDashboardSummary()
  }, [])

  const xLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  const yLabels = [
    '12am',
    '1am',
    '2am',
    '3am',
    '4am',
    '5am',
    '6am',
    '7am',
    '8am',
    '9am',
    '10am',
    '11am',
    '12pm',
    '1pm',
    '2pm',
    '3pm',
    '4pm',
    '5pm',
    '6pm',
    '7pm',
    '8pm',
    '9pm',
    '10pm',
    '11pm',
  ]

  const formatHeatmapData = () => {
    if (!heatmapData || heatmapData.length === 0) return []

    const dataMap = new Map()

    heatmapData.forEach((item: any) => {
      const dayOfWeek = moment(item.order_date).day()

      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1

      const hourIndex = Number.parseInt(item.order_hour)

      const value = Number.parseInt(item.order_count)

      const key = `${dayIndex}-${hourIndex}`

      if (dataMap.has(key)) {
        dataMap.set(key, dataMap.get(key) + value)
      } else {
        dataMap.set(key, value)
      }
    })

    const formattedData: [number, number, number][] = []
    dataMap.forEach((value, key) => {
      const [dayIndex, hourIndex] = key.split('-').map(Number)
      formattedData.push([dayIndex, hourIndex, value])
    })

    return formattedData
  }

  const getMinMax = () => {
    if (!heatmapData || heatmapData.length === 0) return { min: 0, max: 100 }

    const values = heatmapData.map((item) => Number.parseInt(item.order_count))
    const min = Math.min(...values)
    const max = Math.max(...values)

    return {
      min: min === max ? 0 : min,
      max: max === 0 ? 100 : max,
    }
  }

  const { min, max } = getMinMax()

  const chartData = {
    xAxis: xLabels,
    yAxis: yLabels,
    min,
    max,
    series: formatHeatmapData(),
    showLabel: true,
    visualMap: {
      min,
      max,
      orient: 'horizontal',
      left: 'center',
      top: '0',
      calculable: true,
      inRange: {
        color: [
          colors.blue,
          colors.blueLighter,
          colors.blueVeryLight,
          colors.blueVeryLighter,
          colors.yellowLighter,
          colors.orangeLighter,
          colors.orange,
          colors.redLighter,
          colors.red,
          colors.redWarm,
        ],
      },
    },
  }

  const chartOptions = {
    tooltip: {
      show: true,
      position: 'top',
      trigger: 'item',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: 'rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
      extraCssText:
        'box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15); border-radius: 8px; padding: 12px 16px; backdrop-filter: blur(4px);',
      textStyle: {
        color: colors.text,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 14,
      },
      formatter: (params: any) => {
        const value = params.data[2]
        const day = xLabels[params.data[0]]
        const hour = yLabels[params.data[1]]
        const extraData = params.data[3] || {}

        //
        const formattedDate = extraData.date
          ? moment(extraData.date).format('MMM DD, YYYY')
          : ''

        let activityLevel = 'Low'
        const percentage = (value / max) * 100
        if (percentage > 70) activityLevel = 'High'
        else if (percentage > 30) activityLevel = 'Medium'

        let activityColor = colors.green
        if (activityLevel === 'Medium') activityColor = colors.orange
        if (activityLevel === 'Low') activityColor = colors.blue

        const gradientBg = `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(${
          activityLevel === 'High'
            ? '76,175,80,0.1'
            : activityLevel === 'Medium'
            ? '255,152,0,0.1'
            : '33,150,243,0.1'
        }) 100%)`

        let html = `
          <div style="background: ${gradientBg}; margin: -12px -16px; padding: 12px 16px; border-radius: 8px;">
            <div style="font-weight: 600; margin-bottom: 12px; font-size: 16px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid rgba(0,0,0,0.05); padding-bottom: 8px;">
              <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background-color: ${activityColor};"></span>
              <span>${day}, ${hour}</span>
              <span style="margin-left: auto; font-size: 13px; color: ${
                colors.grayText
              };">${formattedDate}</span>
            </div>

            <div style="display: flex; justify-content: space-between; margin-bottom: 8px; align-items: center;">
              <span style="color: ${colors.grayText};">Total Orders:</span>
              <span style="font-weight: 600; font-size: 18px;">${value}</span>
            </div>

            <div style="display: flex; justify-content: space-between; margin-bottom: 12px; align-items: center;">
              <span style="color: ${colors.grayText};">Activity Level:</span>
              <span style="font-weight: 500; color: ${activityColor}; background: rgba(${
          activityLevel === 'High'
            ? '76,175,80,0.1'
            : activityLevel === 'Medium'
            ? '255,152,0,0.1'
            : '33,150,243,0.1'
        }); padding: 2px 8px; border-radius: 12px;">${activityLevel}</span>
            </div>
        `

        // Add time period context
        if (hour.includes('am')) {
          if (Number.parseInt(hour) < 6) {
            html += `<div style="font-size: 13px; color: ${colors.grayText}; margin-top: 8px; padding-top: 8px; border-top: 1px dashed rgba(0,0,0,0.1);">
              <span style="color: ${colors.grayTextLight};">📊 Night period (12am-6am)</span>
            </div>`
          } else {
            html += `<div style="font-size: 13px; color: ${colors.grayText}; margin-top: 8px; padding-top: 8px; border-top: 1px dashed rgba(0,0,0,0.1);">
              <span style="color: ${colors.grayTextLight};">📊 Morning period (6am-12pm)</span>
            </div>`
          }
        } else {
          if (Number.parseInt(hour) < 6) {
            html += `<div style="font-size: 13px; color: ${colors.grayText}; margin-top: 8px; padding-top: 8px; border-top: 1px dashed rgba(0,0,0,0.1);">
              <span style="color: ${colors.grayTextLight};">📊 Afternoon period (12pm-6pm)</span>
            </div>`
          } else {
            html += `<div style="font-size: 13px; color: ${colors.grayText}; margin-top: 8px; padding-top: 8px; border-top: 1px dashed rgba(0,0,0,0.1);">
              <span style="color: ${colors.grayTextLight};">📊 Evening period (6pm-12am)</span>
            </div>`
          }
        }

        if (heatmapData.length > 0) {
          const avgOrders =
            heatmapData.reduce(
              (sum, item) => sum + Number.parseInt(item.order_count),
              0,
            ) / heatmapData.length
          const diff = value - avgOrders
          const percentDiff = (diff / avgOrders) * 100

          const diffText = diff >= 0 ? `+${diff.toFixed(0)}` : diff.toFixed(0)
          const percentText =
            percentDiff >= 0
              ? `+${percentDiff.toFixed(1)}%`
              : `${percentDiff.toFixed(1)}%`
          const diffColor = diff >= 0 ? colors.green : colors.red

          html += `
            <div style="display: flex; justify-content: space-between; margin-top: 8px; font-size: 13px;">
              <span style="color: ${colors.grayText};">vs. Average:</span>
              <span style="font-weight: 500; color: ${diffColor};">${diffText} (${percentText})</span>
            </div>
          `
        }

        html += '</div>'
        return html
      },
    },
    grid: {
      height: '70%',
      top: '60px',
    },
    emphasis: {
      itemStyle: {
        shadowBlur: 10,
        shadowColor: 'rgba(0, 0, 0, 0.5)',
      },
    },
  }

  return (
    <DcDiv className="w-full h-[500px]" style={{ height: '400px' }}>
      <DcChart
        type="heatmap"
        data={chartData}
        options={chartOptions}
        height="690px"
      />
    </DcDiv>
  )
}

export default HeatingHours
